import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { useTranslations } from '@libs/translations';
import { IconArrowDown } from '@icons';
import { pushDataLayer } from '@libs';
import { Points, textSize } from '@styles';
import { HOSTNAME } from '@constants';
import { SUPPORT_LANGS, LANG_CAPTION } from '@constants/langs';

export const Langs = ({ isDarkMode, isTransparent, isScrollUp = false }) => {
  const { locale } = useSelector((state) => state.settings);
  const selectorRef = useRef(null);
  const optionsRef = useRef(null);
  const router = useRouter();
  const translations = useTranslations();
  const [optionsState, setOptionsState] = useState({
    isOpen: false,
    isShow: false,
  });

  const btnClickHandler = () => {
    setOptionsState((prev) => ({ ...prev, isOpen: !optionsState.isOpen }));
    if (optionsState.isOpen) {
      setTimeout(() => {
        setOptionsState((prev) => ({ ...prev, isShow: false }));
      }, 0);
    } else {
      setOptionsState((prev) => ({ ...prev, isShow: true }));
    }
  };

  const handleClickOutside = React.useCallback((event) => {
    requestAnimationFrame(() => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setOptionsState((prev) => ({ ...prev, isOpen: false, isShow: false }));
      }
    });
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const handleScroll = () => {
      setOptionsState((prev) => (prev.isOpen || prev.isShow ? { ...prev, isOpen: false, isShow: false } : prev));
    };

    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onClickItem = (value) => {
    pushDataLayer({
      event: 'ga4_Event',
      evName: 'lang_switch',
      evParam1: `${locale}-${value}`,
    });
    translations.changeLang(value, router);
    btnClickHandler();
  };

  return (
    <View>
      <Selector ref={selectorRef}>
        <CurrentButton
          onClick={btnClickHandler}
          isDarkMode={isDarkMode}
          isScrollUp={isScrollUp}
          isShow={optionsState.isShow}>
          <span>{LANG_CAPTION[locale]}</span>
          <IconArrowDown width={24} height={24} stroke={'color'} />
        </CurrentButton>
        <OptionWrapper isOpen={optionsState.isOpen} isShow={optionsState.isShow}>
          {optionsState.isShow && (
            <Options
              isDarkMode={isDarkMode}
              isTransparent={isTransparent}
              isScrollUp={isScrollUp}
              ref={optionsRef}
              isOpen={optionsState.isOpen}
              isShow={optionsState.isShow}>
              {SUPPORT_LANGS.map(({ value }) => (
                <Item key={value}>
                  <Button
                    onClick={() => onClickItem(value)}
                    isMobile={deviceDetected.isMobile}
                    isDarkMode={isDarkMode}
                    isScrollUp={isScrollUp}
                    isCurrent={locale === value}>
                    {value}
                  </Button>
                </Item>
              ))}
            </Options>
          )}
        </OptionWrapper>
      </Selector>
      <HiddenLinks>
        {SUPPORT_LANGS.map(({ value, title }) => (
          <a key={value} href={'https://' + HOSTNAME + '/' + value + '/'} aria-label={title} />
        ))}
      </HiddenLinks>
    </View>
  );
};

const View = styled.div`
  margin-left: auto;
  padding-top: 2px;
  width: 66px;

  @media ${Points.tablet_landscape} {
    margin-left: 0;
  }
`;

const Selector = styled.div`
  position: relative;
`;

const HiddenLinks = styled.div`
  ${Mixins.visuallyHidden()};
`;

const CurrentButton = styled.button`
  ${Mixins.transition('border-color, color')};
  ${Mixins.resetButton()};
  ${textSize('footer_txt')}
  position: relative;
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: right;
  width: 100%;
  color: ${(p) => (p.isScrollUp ? '#3f3f3f' : p.isDarkMode ? Colors.white : Colors.main_dark)};
  background-color: ${(p) => (p.isScrollUp && p.isShow ? '#ffffff99' : 'transparent')};
  border-radius: ${(p) => (p.isScrollUp ? '9px 9px 0 0' : '0')};
  font-size: 16px;
  line-height: 1.1;
  text-transform: uppercase;
  padding: 5px 5px 5px 10px;

  @media ${Points.tablet_landscape} {
    color: ${(p) => (p.isScrollUp ? '#3f3f3f' : p.isDarkMode ? Colors.white : Colors.main_dark)};
  }

  svg {
    rotate: ${(p) => p.isShow && '180deg'};
    transition: rotate 0.25s;
    stroke: ${(p) => (p.isScrollUp ? '#3f3f3f' : p.isDarkMode ? Colors.white : Colors.main_dark)};
  }
`;

const OptionWrapper = styled.div`
  position: absolute;
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  opacity: ${(p) => (p.isShow ? 1 : 0)};
`;

const Options = styled.ul`
  ${Mixins.resetList()};
  width: 100%;
  border-radius: 0 0 9px 9px;
  background-color: ${(p) =>
    p.isScrollUp
      ? '#ffffff91'
      : p.isDarkMode && !p.isTransparent
      ? Colors.main_black
      : p.isTransparent
      ? 'transparent'
      : Colors.white};
  backdrop-filter: ${(p) => p.isTransparent && `blur(5px) saturate(15%)`};
  -webkit-backdrop-filter: ${(p) => p.isTransparent && `blur(18px) saturate(15%)`};
  padding: ${(p) => (p.isScrollUp ? '0 10px 5px' : '0px 34px 5px 10px')};
`;

const Item = styled.li`
  ${textSize('footer_txt')}
  font-weight: normal;
  pointerevents: ${(p) => (p.isOpen ? 'auto' : 'none')};
`;

const Button = styled.button`
  ${Mixins.transition('border-color')};
  ${Mixins.resetButton()};
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 5px 0;
  text-transform: uppercase;
  color: ${(p) =>
    p.isScrollUp ? '#3f3f3f' : p.isCurrent ? (p.isDarkMode ? Colors.white : Colors.main_dark) : Colors.dark_500};

  @media ${Points.laptop} {
    padding: 5px 0;
  }

  &:hover,
  &:active {
    color: ${(p) => (p.isScrollUp ? '#3f3f3f' : p.isDarkMode ? Colors.white : Colors.main_dark)};
  }

  img {
    display: block;
    margin-right: 5px;
    width: 14px;
  }
`;
